import type { ProductLabel } from './productLabel';
import type { HasProductFields, HasVariantFields, ProductCardData } from './toProductCard';

import { buildVariantMap, getFirstVariant } from './accessors';
import { toProductCard } from './toProductCard';

type HasProductWithVariants = HasProductFields & {
  readonly variants: readonly (HasVariantFields & {
    readonly labels?: readonly ProductLabel[] | undefined;
  })[];
};

/**
 * `code` and `routerUrl` stay bare `string`, not the branded `ProductCode` /
 * `ProductRouterUrl` from `@vs-shop/schemas/branded/ProductSchema`: the
 * generated `suggestions`/`sets` item shape resolves through
 * `ProductShowBase.suggestions`/`.sets` (`Schema.Unknown` overrides in
 * `packages/schemas/src/branded/ProductSchema.ts`), which is not precise
 * enough at this nesting to support the brand — asserting it here would not
 * be proven by the schema. Branding it for real needs `productTightened.ts`
 * to tighten the nested `suggestions`/`sets` item shape, a schema change
 * rather than a type annotation.
 */
type HasAccessoryItemFields = {
  readonly product?:
    | {
        readonly code?: string | undefined;
        readonly routerUrl?: string | undefined;
        readonly ageRestriction?: 'none' | '18+' | undefined;
        readonly name?: string | null | undefined;
        readonly metaTitle?: string | null | undefined;
        readonly slug?: string | null | undefined;
        readonly shortDescription?: string | null | undefined;
        readonly subtitle?: string | null | undefined;
        readonly mainImage?:
          | { readonly pathAbstract?: string | null | undefined }
          | null
          | undefined;
      }
    | null
    | undefined;
  readonly label?: string | null | undefined;
  readonly quantity?: number | undefined;
};

/**
 * Derived from `toProductCard`'s own return type rather than naming
 * `ProductCardData` directly, so this stays correct for free whenever that
 * mapper's output type changes.
 */
export type AccessoryCardData = ReturnType<typeof toProductCard> & {
  readonly label: string | null;
  readonly quantity: number;
};

export function mapProductsToCards(products: readonly HasProductWithVariants[]): ProductCardData[] {
  return products.map((product) => toProductCard(product, getFirstVariant(product.variants)));
}

export function productToCard(product: HasProductWithVariants): ProductCardData {
  return toProductCard(product, getFirstVariant(product.variants));
}

export function mapVariantAssociationsToCards(
  associations: readonly HasProductFields[],
  variantDetails: readonly (HasVariantFields & {
    readonly code: string;
    readonly labels?: readonly ProductLabel[] | undefined;
  })[]
): ProductCardData[] {
  const variantMap = buildVariantMap(variantDetails);
  return associations.map((assoc) => {
    const variant = variantMap.get(assoc.code) ?? null;
    return toProductCard(assoc, variant);
  });
}

export function mapAccessoryItemsToCards(
  items: readonly HasAccessoryItemFields[],
  variantDetails: readonly (HasVariantFields & {
    readonly code: string;
    readonly labels?: readonly ProductLabel[] | undefined;
  })[]
): AccessoryCardData[] {
  const variantMap = buildVariantMap(variantDetails);
  return items.map((item) => {
    const product = item.product;
    const variant = variantMap.get(product?.code ?? '') ?? null;
    return {
      ...toProductCard(
        {
          code: product?.code ?? '',
          routerUrl: product?.routerUrl ?? '',
          ageRestriction: product?.ageRestriction ?? 'none',
          name: product?.name ?? null,
          metaTitle: product?.metaTitle ?? null,
          slug: product?.slug ?? null,
          shortDescription: product?.shortDescription ?? null,
          subtitle: product?.subtitle ?? null,
          mainImage: product?.mainImage ?? null,
        },
        variant
      ),
      label: item.label ?? null,
      quantity: item.quantity ?? 1,
    };
  });
}
